export const VAULT_TRANSLATION_SUPPORT = { startPercent: 50, stepPercent: 10, minPercent: 0, maxPercent: 100 } as const;

export function clampVaultTranslationPercent(value: number | null | undefined): number {
    return typeof value === "number" && Number.isFinite(value)
        ? Math.max(VAULT_TRANSLATION_SUPPORT.minPercent, Math.min(VAULT_TRANSLATION_SUPPORT.maxPercent, Math.round(value)))
        : VAULT_TRANSLATION_SUPPORT.startPercent;
}

/** One accepted answer moves support; misses without a submitted answer are unknown. */
export function adjustVaultTranslationPercent(value: number | null | undefined, correct: boolean): number {
    return clampVaultTranslationPercent(clampVaultTranslationPercent(value) +
        (correct ? -1 : 1) * VAULT_TRANSLATION_SUPPORT.stepPercent);
}

/** Each crack uses its frozen personal window and the authoritative clock. */
export function getVaultPersonalTranslationOpacity(start: number | null, end: number | null, now: number,
    durationFraction: number): number {
    if (start === null || end === null || end <= start || now < start) return 0;
    const fraction = Number.isFinite(durationFraction) ? Math.max(0, Math.min(1, durationFraction)) : .5;
    if (fraction === 0) return 0;
    if (fraction === 1) return 1;
    return Math.max(0, Math.min(1, 1 - (now - start) / ((end - start) * fraction)));
}

/** Legacy clients/in-flight cracks: competition rank normalized from last (0) to first (1). */
export function getVaultTranslationRanks(players: readonly { sessionId: string; vaultScore: number }[]): Record<string, number> {
    const scores = players.map(player => player.vaultScore).filter(Number.isFinite);
    const bottomRank = scores.filter(score => score > Math.min(...scores)).length;
    return Object.fromEntries(players.map(player => [player.sessionId,
        bottomRank === 0 || !Number.isFinite(player.vaultScore) ? 0
            : Math.max(0, Math.min(1, 1 - scores.filter(score => score > player.vaultScore).length / bottomRank))
    ]));
}

/** Absolute server time prevents rerenders, reloads and background tabs restarting a fade. */
export function getVaultTranslationOpacity(start: number | null, end: number | null, now: number,
    rankFraction = 0, leaderDurationFraction = 0.5): number {
    if (start === null || end === null || end <= start || now < start) return 0;
    const rank = Number.isFinite(rankFraction) ? Math.max(0, Math.min(1, rankFraction)) : 0;
    // Last place (also solo/all-tied/legacy viewers) retains fully readable support.
    if (rank === 0) return 1;
    const duration = (end - start) * (1 - rank * (1 - leaderDurationFraction));
    return Math.max(0, Math.min(1, 1 - (now - start) / duration));
}
